import {CampaignStep} from '../campaignWorkflow/campaignWorkflow.enums';
import {accountPrefix} from './routing.consts';
import {PathParamType, QueryParamType, RouteKey} from './routing.enums';
import {type PathParamDef, type QueryParamDef, type RouteDef} from './routing.types';
import routeDef from './utils/routeDef';

const campaignIdPathParam: PathParamDef = {
  key: 'campaignId',
  type: PathParamType.Number,
  displayName: 'Campaign ID',
};

const versionIdPathParam: PathParamDef = {
  key: 'versionId',
  type: PathParamType.Number,
  displayName: 'Version ID',
};

const versionNumberPathParam: PathParamDef = {
  key: 'versionNumber',
  type: PathParamType.String,
  defaultValue: 'latest',
  displayName: 'Version Number',
};

const creativeIdPathParam: PathParamDef = {
  key: 'creativeId',
  type: PathParamType.Number,
  displayName: 'Creative ID',
};

const templateIdPathParam: PathParamDef = {
  key: 'templateId',
  type: PathParamType.Number,
  displayName: 'Template ID',
};

const widgetIdPathParam: PathParamDef = {
  key: 'widgetId',
  type: PathParamType.Number,
  displayName: 'Widget ID',
};

const feedIdPathParam: PathParamDef = {
  key: 'feedId',
  type: PathParamType.Number,
  displayName: 'Feed ID',
};

const projectIdPathParam: PathParamDef = {
  key: 'projectId',
  type: PathParamType.Number,
  displayName: 'Project ID',
};

const dashboardIdPathParam: PathParamDef = {
  key: 'dashboardId',
  type: PathParamType.Number,
  displayName: 'Dashboard ID',
};

const articleIdPathParam: PathParamDef = {
  key: 'articleId',
  type: PathParamType.Number,
  displayName: 'Article ID',
};

const slugPathParam: PathParamDef = {
  key: 'slug',
  type: PathParamType.String,
  displayName: 'Slug',
};

const tokenPathParam: PathParamDef = {
  key: 'token',
  type: PathParamType.String,
  displayName: 'Token',
};

const tearsheetIdPathParam: PathParamDef = {
  key: 'tearsheetId',
  type: PathParamType.Number,
  displayName: 'Tearsheet ID',
};

const userTypePathParam: PathParamDef = {
  key: 'userType',
  type: PathParamType.String,
  displayName: 'User Type',
};

const stepsQueryParam: QueryParamDef = {
  key: 'steps',
  type: QueryParamType.OneOrMoreOf,
  enumValues: Object.values(CampaignStep),
  required: false,
  displayName: 'Steps',
  defaultValue: CampaignStep.Home,
};

// All route definitions
const allRouteDefinitions: Record<RouteKey, RouteDef> = {
  [RouteKey.BaseRoute]: routeDef({
    path: '/',
    displayName: '',
  }),

  [RouteKey.Playground]: routeDef({
    path: '/playground',
    displayName: 'Playground Page',
  }),

  [RouteKey.__DEPRECATED__Login]: routeDef({
    path: '/sso',
    displayName: 'Login Page',
  }),

  [RouteKey.Login]: routeDef({
    path: '/login',
    displayName: 'Login Page',
  }),

  [RouteKey.Logout]: routeDef({
    path: '/logout',
    displayName: 'Logout Page',
  }),

  [RouteKey.UpdatePassword]: routeDef({
    path: '/update-password',
    displayName: 'Update Password Page',
  }),

  [RouteKey.ForgotPassword]: routeDef({
    path: '/forgot-password',
    displayName: 'Forgot Password Page',
  }),

  [RouteKey.ChangePassword]: routeDef({
    path: '/change-password',
    displayName: 'Change Password Page',
  }),

  [RouteKey.SSORedirect]: routeDef({
    path: '/sso-login/:token',
    displayName: 'SSO Redirect Page',
    pathParams: [tokenPathParam],
  }),

  [RouteKey.ContactUs]: routeDef({
    path: '/contact-us',
    displayName: 'Contact Us Page',
  }),

  [RouteKey.Campaigns]: routeDef({
    path: '/campaigns',
    displayName: 'Campaigns Library Page',
  }),

  [RouteKey.Notifications]: routeDef({
    path: '/notifications',
    displayName: 'Notifications Page',
  }),

  [RouteKey.NotificationsDefinitions]: routeDef({
    path: '/notifications/definitions',
    displayName: 'Notifications Definitions Page',
  }),

  [RouteKey.Advertisers]: routeDef({
    path: '/advertisers',
    displayName: 'Advertisers Page',
  }),

  [RouteKey.Billing]: routeDef({
    path: '/clinch-admin/billing',
    displayName: 'Billing Page',
  }),

  [RouteKey.Usage]: routeDef({
    path: '/clinch-admin/usage',
    displayName: 'Usage Page',
  }),

  [RouteKey.AccountSettings]: routeDef({
    path: '/settings/account-settings',
    displayName: 'Account Settings Page',
  }),

  [RouteKey.Dashboard]: routeDef({
    path: '/dashboard',
    displayName: 'Account Dashboard Page',
  }),

  [RouteKey.DashboardInfo]: routeDef({
    path: '/dashboard/:dashboardId',
    displayName: 'Dashboard Info Page',
    pathParams: [dashboardIdPathParam],
  }),

  [RouteKey.Metrics]: routeDef({
    path: '/analytics/metrics',
    displayName: 'Metrics Page',
  }),

  [RouteKey.AudienceInsights]: routeDef({
    path: '/analytics/audience-insights',
    displayName: 'Audience Insights Page',
  }),

  [RouteKey.Reports]: routeDef({
    path: '/analytics/my-reports',
    displayName: 'My Reports Page',
  }),

  [RouteKey.OutputDataFeeds]: routeDef({
    path: '/analytics/output-data-feeds',
    displayName: 'Output Data Feeds Page',
  }),

  [RouteKey.CampaignBrief]: routeDef({
    path: '/campaigns/:campaignId/v/:versionId/brief',
    displayName: 'Campaign Brief Page',
    pathParams: [campaignIdPathParam, versionIdPathParam],
  }),

  [RouteKey.CampaignBriefPreview]: routeDef({
    path: '/campaigns/:campaignId/v/:versionId/brief/preview',
    displayName: 'Campaign Brief Preview Page',
    pathParams: [campaignIdPathParam, versionIdPathParam],
  }),

  [RouteKey.AccountIntegrations]: routeDef({
    path: '/settings/integrations',
    displayName: 'Account Integrations Page',
  }),

  [RouteKey.UserRules]: routeDef({
    path: '/settings/user-rules',
    displayName: 'User Rules Page',
  }),

  [RouteKey.AccountPermissions]: routeDef({
    path: '/settings/permissions',
    displayName: 'Account Permissions Page',
  }),

  [RouteKey.UserPermissions]: routeDef({
    path: '/settings/users',
    displayName: 'User Permissions Page',
  }),

  [RouteKey.AllUsers]: routeDef({
    path: '/settings/all-users',
    displayName: 'All Users Page',
  }),

  [RouteKey.LinkedAgencies]: routeDef({
    path: '/settings/linked-agencies',
    displayName: 'Linked Agencies Page',
  }),

  [RouteKey.AuditTrail]: routeDef({
    path: '/settings/audit-trail',
    displayName: 'Audit Trail Page',
  }),

  [RouteKey.UsageLimits]: routeDef({
    path: '/settings/usage-limits',
    displayName: 'Usage & Limits Page',
  }),

  [RouteKey.DataSources]: routeDef({
    path: '/settings/data-sources',
    displayName: 'Data Sources Page',
  }),

  [RouteKey.RepositoryTargets]: routeDef({
    path: '/settings/repository-targets',
    displayName: 'Repository Targets Page',
  }),

  [RouteKey.AccountDspEditor]: routeDef({
    path: '/settings/dsp-editor',
    displayName: 'Account DSP Editor Page',
  }),

  [RouteKey.DoohScreens]: routeDef({
    path: '/settings/dooh-screens',
    displayName: 'DOOH Screens Page',
  }),

  [RouteKey.ExternalAuthIntegration]: routeDef({
    path: '/clinch-admin/integrations/external-auth',
    displayName: 'External Auth Integration Page',
  }),

  [RouteKey.AccountsManagement]: routeDef({
    path: '/clinch-admin/accounts-management',
    displayName: 'Accounts Management Page',
  }),

  [RouteKey.UserProfile]: routeDef({
    path: '/clinch-admin/user-profile',
    displayName: 'User Profile Page',
  }),

  [RouteKey.ClientsAnalytics]: routeDef({
    path: '/clinch-admin/clients-analytics',
    displayName: 'Clients Analytics Page',
  }),

  [RouteKey.DspLibrary]: routeDef({
    path: '/clinch-admin/dsp-editor',
    displayName: 'Account DSPs and Publishers Page',
  }),

  [RouteKey.TrackingVendors]: routeDef({
    path: '/clinch-admin/tracking-vendors',
    displayName: 'Tracking Vendors Page',
  }),

  [RouteKey.FeatureFlags]: routeDef({
    path: '/clinch-dev/feature-flags',
    displayName: 'Feature Flags Page',
  }),

  [RouteKey.JobsManagement]: routeDef({
    path: '/clinch-dev/jobs-management',
    displayName: 'Jobs Management Page',
  }),

  [RouteKey.ServicesHealth]: routeDef({
    path: '/clinch-dev/services-health',
    displayName: 'Services Monitor Page',
  }),

  [RouteKey.IssuesDocumentation]: routeDef({
    path: '/clinch-dev/issues-documentation',
    displayName: 'Issues Documentation Page',
  }),

  [RouteKey.KnowledgeCenter]: routeDef({
    path: '/help/knowledge-center',
    displayName: 'Knowledge Center Page',
  }),

  [RouteKey.KnowledgeCenterArticle]: routeDef({
    path: '/help/knowledge-center/:articleId/:slug',
    displayName: 'Knowledge Center Article Page',
    pathParams: [articleIdPathParam, slugPathParam],
  }),

  [RouteKey.KnowledgeCenterSearch]: routeDef({
    path: '/help/knowledge-center/search',
    displayName: 'Knowledge Center Search Page',
  }),

  [RouteKey.FlightAcademy]: routeDef({
    path: '/help/flight-academy',
    displayName: 'Flight Academy Page',
  }),

  [RouteKey.CreativePage]: routeDef({
    path: '/libraries/creative',
    displayName: 'Creative Page',
  }),

  [RouteKey.WidgetsLibrary]: routeDef({
    path: '/libraries/widgets',
    displayName: 'Widgets Library Page',
  }),

  [RouteKey.WidgetEdit]: routeDef({
    path: '/libraries/widgets/:widgetId',
    displayName: 'Creative Widget Editor Page',
    pathParams: [widgetIdPathParam],
  }),

  [RouteKey.TemplateDynamicParameters]: routeDef({
    path: '/:userType/creative-templates/:templateId',
    displayName: 'Creative Template Dynamic Parameters Page',
    pathParams: [userTypePathParam, templateIdPathParam],
  }),

  [RouteKey.CreativeTemplatePreview]: routeDef({
    path: '/libraries/creative-template-preview/:templateId',
    displayName: 'Creative Template Preview Page',
    pathParams: [templateIdPathParam],
  }),

  [RouteKey.CreativePreview]: routeDef({
    path: '/libraries/creative-preview/:creativeId',
    displayName: 'Creative Preview Page',
    pathParams: [creativeIdPathParam],
  }),

  [RouteKey.PublicCreativePreview]: routeDef({
    path: '/creative-preview/:creativeId',
    displayName: 'Public Creative Preview Page',
    pathParams: [creativeIdPathParam],
  }),

  [RouteKey.CampaignTearSheetPreview]: routeDef({
    path: '/campaign/:campaignId/v/:versionId/tearsheet/:tearsheetId',
    displayName: 'Campaign Tearsheet Preview Page',
    pathParams: [campaignIdPathParam, versionIdPathParam, tearsheetIdPathParam],
  }),

  [RouteKey.CampaignTearSheetDraftPreview]: routeDef({
    path: '/campaign/:campaignId/v/:versionId/tearsheet/draft',
    displayName: 'Campaign Tearsheet Draft Preview Page',
    pathParams: [campaignIdPathParam, versionIdPathParam],
  }),

  [RouteKey.CreativesLibrary]: routeDef({
    path: '/libraries/creatives',
    displayName: 'Creatives Library Page',
  }),

  [RouteKey.CreativeDynamicParameters]: routeDef({
    path: '/libraries/creatives/:creativeId',
    displayName: 'Creative Dynamic Parameters Page',
    pathParams: [creativeIdPathParam],
  }),

  [RouteKey.CreativeTemplatesLibrary]: routeDef({
    path: '/libraries/creative-templates',
    displayName: 'Creative Templates Library Page',
  }),

  [RouteKey.LocationFeedsLibrary]: routeDef({
    path: '/libraries/location-feeds',
    displayName: 'Location Feeds Library Page',
  }),

  [RouteKey.LocationFeed]: routeDef({
    path: '/libraries/location-feeds/:feedId',
    displayName: 'Location Feed Page',
    pathParams: [feedIdPathParam],
  }),

  [RouteKey.ProductFeedsLibrary]: routeDef({
    path: '/libraries/product-feeds',
    displayName: 'Product Feeds Library Page',
  }),

  [RouteKey.ProductFeed]: routeDef({
    path: '/libraries/product-feeds/:feedId',
    displayName: 'Product Feed Page',
    pathParams: [feedIdPathParam],
  }),

  [RouteKey.AssetsFileManager]: routeDef({
    path: '/libraries/assets-manager',
    displayName: 'Assets File Manager Page',
  }),

  [RouteKey.FontsLibrary]: routeDef({
    path: '/libraries/fonts-manager',
    displayName: 'Fonts Library Page',
  }),

  [RouteKey.AttributionSignals]: routeDef({
    path: '/settings/attributions-setup',
    displayName: 'Attributions Setup Page',
  }),

  [RouteKey.BrandGuidelines]: routeDef({
    path: '/settings/brand-guidelines',
    displayName: 'Brand Guidelines Page',
  }),

  [RouteKey.AudiencesLibrary]: routeDef({
    path: '/libraries/audiences',
    displayName: 'Audiences Library Page',
  }),

  [RouteKey.DynamicSourceOptions]: routeDef({
    path: '/clinch-admin/dynamic-source-options',
    displayName: 'Dynamic Source Options Page',
  }),

  [RouteKey.MyProfile]: routeDef({
    path: '/my-profile',
    displayName: 'My Profile Page',
  }),

  [RouteKey.UserSignUp]: routeDef({
    path: '/signup',
    displayName: 'User Sign Up Page',
  }),

  [RouteKey.TearsheetExportMain]: routeDef({
    path: '/tearsheet/export/main',
    displayName: 'Tearsheet Export Page',
  }),

  [RouteKey.TearsheetExportDisplayVariation]: routeDef({
    path: '/tearsheet/export/display',
    displayName: 'Tearsheet Export Display Variation Page',
  }),

  [RouteKey.TearsheetExportVideoVariation]: routeDef({
    path: '/tearsheet/export/video',
    displayName: 'Tearsheet Export Video Variation Page',
  }),

  [RouteKey.TemplateTearsheetExportVideoVariation]: routeDef({
    path: '/template-tearsheet/export/video',
    displayName: 'Template Tearsheet Export Video Variation Page',
  }),

  [RouteKey.ErrorCodes]: routeDef({
    path: '/admin/errorcodes',
    displayName: 'Error Codes Page',
  }),

  [RouteKey.NoPermissions]: routeDef({
    path: '/no-permissions',
    displayName: 'No Permissions Page',
  }),

  // Campaign Workflow
  [RouteKey.CampaignPage]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber',
    displayName: 'Campaign Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
    queryParams: [stepsQueryParam],
  }),

  [RouteKey.CampaignWorkflowBrief]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/brief',
    displayName: 'Campaign Brief Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowPlacements]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/placements',
    displayName: 'Campaign Placements Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowTrackers]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/trackers',
    displayName: 'Campaign Trackers Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowConceptPlan]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/concept-plan',
    displayName: 'Campaign Concept Plan Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowCampaignLogic]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/campaign-logic',
    displayName: 'Campaign Campaign Logic Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowAds]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/ads',
    displayName: 'Campaign Ads Table Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowTearsheetList]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/tearsheet',
    displayName: 'Campaign Tearsheet Table Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowTearsheet]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/tearsheet/:tearsheetId',
    displayName: 'Campaign Tearsheet Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam, tearsheetIdPathParam],
  }),

  [RouteKey.CampaignWorkflowActivation]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/activation',
    displayName: 'Campaign Activation Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowInsights]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/insights',
    displayName: 'Campaign Insights Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowAdVariationsAnalysis]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/ad-variations-analysis',
    displayName: 'Campaign Ad Variations Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignWorkflowVariations]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/variations',
    displayName: 'Campaign Video Ad Variations Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignPagePublic]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/public',
    displayName: 'Campaign Public Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignLogicPublic]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/public/campaign-logic',
    displayName: 'Campaign Logic Public Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.ConceptPlanPublic]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/public/concept-plan',
    displayName: 'Campaign Concept Plan Public Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  [RouteKey.CampaignAdsPublic]: routeDef({
    path: '/campaigns/:campaignId/v/:versionNumber/public/ads',
    displayName: 'Campaign Ads Table Public Page',
    pathParams: [campaignIdPathParam, versionNumberPathParam],
  }),

  // Template Workflow
  [RouteKey.TemplateWorkflow]: routeDef({
    path: '/libraries/creative-template/:templateId',
    displayName: 'Creative Template Page',
    pathParams: [templateIdPathParam],
  }),

  [RouteKey.TemplateWorkflowBuilder]: routeDef({
    path: '/libraries/creative-template/:templateId/ad-builder',
    displayName: 'Creative Template Editor Page',
    pathParams: [templateIdPathParam],
  }),

  [RouteKey.TemplateWorkflowMap]: routeDef({
    path: '/libraries/creative-template/:templateId/map',
    displayName: 'Creative Template Map Page',
    pathParams: [templateIdPathParam],
  }),

  [RouteKey.TemplateWorkflowSet]: routeDef({
    path: '/libraries/creative-template/:templateId/set',
    displayName: 'Creative Template Set Page',
    pathParams: [templateIdPathParam],
  }),

  [RouteKey.TemplateWorkflowTearsheetList]: routeDef({
    path: '/libraries/creative-template/:templateId/tearsheet',
    displayName: 'Creative Template Tearsheet Table Page',
    pathParams: [templateIdPathParam],
  }),

  [RouteKey.TemplateWorkflowTearsheet]: routeDef({
    path: '/libraries/creative-template/:templateId/tearsheet/:tearsheetId',
    displayName: 'Creative Template Tearsheet Page',
    pathParams: [templateIdPathParam, tearsheetIdPathParam],
  }),

  [RouteKey.TemplateWorkflowTearsheetPreview]: routeDef({
    path: '/libraries/creative-template/:templateId/tearsheet-preview/:tearsheetId',
    displayName: 'Creative Template Tearsheet Preview Page',
    pathParams: [templateIdPathParam, tearsheetIdPathParam],
  }),

  // Content Feed Workflow
  [RouteKey.ContentFeedsLibrary]: routeDef({
    path: '/libraries/content-feeds',
    displayName: 'Content Feeds Library Page',
  }),

  [RouteKey.ContentFeedPage]: routeDef({
    path: '/libraries/content-feeds/:feedId',
    displayName: 'Content Feed Page',
    pathParams: [feedIdPathParam],
  }),

  [RouteKey.ContentFeedItems]: routeDef({
    path: '/libraries/content-feeds/:feedId/items',
    displayName: 'Content Feed Items Page',
    pathParams: [feedIdPathParam],
  }),

  [RouteKey.ContentFeedMapping]: routeDef({
    path: '/libraries/content-feeds/:feedId/mapping',
    displayName: 'Content Feed Mapping Page',
    pathParams: [feedIdPathParam],
  }),

  [RouteKey.ContentFeedSets]: routeDef({
    path: '/libraries/content-feeds/:feedId/content-sets',
    displayName: 'Content Feed Sets Page',
    pathParams: [feedIdPathParam],
  }),

  [RouteKey.AiContentProjectsLibrary]: routeDef({
    path: '/libraries/ai-content-projects',
    displayName: 'AI Content Projects Library Page',
  }),

  [RouteKey.AiContentProjectPage]: routeDef({
    path: '/libraries/ai-content-projects/:projectId',
    displayName: 'AI Content Project Page',
    pathParams: [projectIdPathParam],
  }),

  [RouteKey.TargetPlatforms]: routeDef({
    path: '/clinch-admin/target-platforms',
    displayName: 'Target Platforms Page',
  }),
};

// Create routes data from route definitions
const RoutesData = Object.freeze({
  ...Object.fromEntries(
    Object.entries(RouteKey).map(([key, route]) => [key, allRouteDefinitions[route].path]),
  ),
} as const);

// Base paths for different sections
const BasePathData = {
  Campaigns: '/campaigns',
  Settings: '/settings',
  Analytics: '/analytics',
  Help: '/help',
  Notifications: '/notifications',
  Authoring: '/libraries',
  Administration: '/clinch-admin',
  Feeds: '/feeds',
  ClinchDev: '/clinch-dev',
  LocationFeeds: '/libraries/location-feeds',
  ProductFeeds: '/libraries/product-feeds',
  ContentFeeds: '/libraries/content-feeds',
} as const;

// Define public routes that don't require account prefix
const PublicRoutes = [
  RoutesData.BaseRoute,
  RoutesData.Playground,
  RoutesData.__DEPRECATED__Login,
  RoutesData.Login,
  RoutesData.SSORedirect,
  RoutesData.PublicCreativePreview,
  RoutesData.Logout,
  RoutesData.ForgotPassword,
  RoutesData.UpdatePassword,
  RoutesData.ContactUs,
  RoutesData.CampaignTearSheetPreview,
  RoutesData.CampaignTearSheetDraftPreview,
  RoutesData.ErrorCodes,
  RoutesData.KnowledgeCenter,
  RoutesData.KnowledgeCenterArticle,
  RoutesData.KnowledgeCenterSearch,
  RoutesData.UserSignUp,
  RoutesData.TearsheetExportMain,
  RoutesData.TearsheetExportDisplayVariation,
  RoutesData.TearsheetExportVideoVariation,
  RoutesData.TemplateTearsheetExportVideoVariation,
  RoutesData.CampaignBriefPreview,
  RoutesData.NoPermissions,
  RoutesData.CampaignPagePublic,
  RoutesData.CampaignLogicPublic,
  RoutesData.ConceptPlanPublic,
  RoutesData.CampaignAdsPublic,
  RoutesData.TemplateWorkflowTearsheetPreview,
] as const;

// Helper function to add account prefix to routes
function addAccountPrefix(target: typeof RoutesData, routeKey: string | symbol): string {
  if (typeof routeKey === 'symbol') return '';

  const url = target[routeKey];
  const isPublic = PublicRoutes.includes(url);

  return isPublic ? url : accountPrefix + url;
}

// Create proxy objects for route access
const Routes = new Proxy({...RoutesData}, {get: addAccountPrefix});

const BasePath = new Proxy(
  {...BasePathData},
  {
    get: (obj: typeof BasePathData, key: keyof typeof BasePathData) =>
      `${accountPrefix}${obj[key]}`,
  },
);

export {allRouteDefinitions, BasePath, BasePathData, PublicRoutes, Routes, RoutesData};
